import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, Subscription, timer } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import {
  NotificationCatalog, NotificationCatalogEntry, NotificationProduct,
} from '../models/notifications/notification-catalog-entry.model';
import { APIService } from './api/api.service';

/**
 * Owns the header bell's state (spec §11.2): whether the Notification Center is on for this API
 * (catalog 200 vs 404), and the caller's unread count, polled every 60 s while started and
 * refreshed on demand after mark-read / navigation. stop() on tenant switch AND logout.
 *
 * Hardening (Codex challenge #12–#14): a failed count never ends the timer (catchError inside the
 * switchMap), and a tenant switch cancels the in-flight catalog call and invalidates its late
 * response through a generation counter.
 *
 * `start()`'s guard reads as one condition but is two guards of very different weight: the
 * `this.catalogSub && !this.catalogSub.closed` half is LOAD-BEARING — it fixes a real bug (see its
 * declaration comment) and is exercised by the refresh-retry test below. `generation` and the
 * `!this.polling.closed` half of the same guard are declared defence-in-depth and are NOT exercised
 * by the current test suite — see the comments at their declarations for why they cannot fire
 * today. Do not "clean up" the load-bearing half because it resembles the other two.
 */
@Injectable({ providedIn: 'root' })
export class NotificationBellService {
  public readonly enabled$: Observable<boolean>;
  public readonly unreadCount$: Observable<number>;

  private static readonly pollMs: number = 60000;
  private readonly enabledSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private readonly unreadSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  /**
   * True once a catalog attempt has settled the question "is the feature on?" for the current
   * tenant — a 200 (on) or a 404 (off, per spec §11.2) — false while unresolved (never attempted,
   * or the last attempt errored with something other than 404). `refresh()` reads this to decide
   * whether a transient catalog failure is worth retrying; a 404 must stay settled and never retry.
   */
  private catalogKnown: boolean = false;
  /**
   * The catalog body the enable-check already fetches, retained instead of discarded. The bell's
   * items need it: a delivery row carries `eventType` and no product column, so the source line
   * spec 11.2 asks for ("{product} - {event label}") cannot be rendered without it. Keeping it
   * here costs no extra request - `start()` fetches this exact payload to decide `enabled$`.
   * Cleared by `stop()`, because the next tenant's catalog can differ.
   */
  private catalog: NotificationCatalog | undefined;
  private catalogSub: Subscription | undefined;
  /**
   * Second line of defence against a late `getCatalog()` response reaching the handlers after a
   * tenant switch: bumped by `stop()`, checked in both the `error` and `next` handlers below.
   * CANNOT currently fire — `stop()` increments this and unconditionally unsubscribes `catalogSub`
   * in the same synchronous call, in that order; the order is inconsequential because there is no
   * async yield point between the two statements, so nothing can observe `catalogSub` still
   * subscribed after `generation` has already moved on. Either way, a stale response is always
   * cancelled at the RxJS layer, and `start()` refuses to start a second catalog call while one is
   * already in flight (see the guard below), so there is never a second in-flight call for a bumped
   * generation to race against. Keep this: if either of those two guards is ever relaxed (e.g.
   * `stop()` becomes async, or `start()` allows overlapping catalog calls), this branch becomes
   * live and needs its own test.
   */
  private generation: number = 0;
  private polling: Subscription | undefined;

  public constructor(private readonly api: APIService) {
    this.enabled$ = this.enabledSubject.asObservable();
    this.unreadCount$ = this.unreadSubject.asObservable();
  }

  public start(): void {
    // `!this.polling.closed` is also unreachable today: `catchError` inside the polling pipe below
    // means the timer stream never errors, so it never completes/closes on its own — only `stop()`
    // closes it, and `stop()` always clears `this.polling` to undefined in the same call. Kept as
    // defence-in-depth for the same reason as `generation` above.
    //
    // `this.catalogSub` is checked the same way (truthy AND not closed), not just truthy: when
    // `getCatalog()` resolves synchronously (a mocked observable in tests; never true HTTP), the
    // handler below sets `this.catalogSub = undefined` DURING the `subscribe()` call, and the
    // outer `this.catalogSub = ....subscribe(...)` assignment then overwrites that with the
    // (already closed) Subscription object once `subscribe()` returns — a plain truthy check would
    // see that stale closed object and refuse to start ever again, which is exactly the failure
    // `refresh()`'s catalog retry (below) would otherwise hit on its very first attempt.
    if ((this.polling && !this.polling.closed) || (this.catalogSub && !this.catalogSub.closed)) {
      return;
    }
    const gen: number = ++this.generation;
    this.catalogSub = this.api.notifications.getCatalog().subscribe({
      error: (error: HttpErrorResponse): void => {
        this.catalogSub = undefined;
        if (gen !== this.generation) {
          return;
        }
        if (error?.status === 404) {
          this.catalogKnown = true;
          this.enabledSubject.next(false);
        }
      },
      next: (catalog: NotificationCatalog): void => {
        this.catalogSub = undefined;
        if (gen !== this.generation) {
          return;
        }
        // AFTER the guard, not before it. `generation` exists to stop a late getCatalog() response
        // reaching these handlers after a tenant switch, and assigning above it would have written
        // the previous tenant's catalog for the new one - other products' labels in the bell -
        // which is the exact scenario the guard covers. Not reachable today (stop() bumps the
        // generation and unsubscribes in one synchronous call), but the field's own remark says to
        // keep the defence intact for when that stops being true.
        this.catalog = catalog;
        this.catalogKnown = true;
        this.enabledSubject.next(true);
        this.polling = timer(0, NotificationBellService.pollMs)
          .pipe(switchMap((): Observable<number | undefined> =>
            this.api.notifications.getUnreadCount().pipe(
              catchError((): Observable<undefined> => of(undefined))
            )))
          .subscribe((n: number | undefined): void => {
            if (n !== undefined) {
              this.unreadSubject.next(n);
            }
          });
      }
    });
  }

  public stop(): void {
    this.generation++;
    this.catalogSub?.unsubscribe();
    this.catalogSub = undefined;
    this.polling?.unsubscribe();
    this.polling = undefined;
    this.catalogKnown = false;
    this.catalog = undefined;
    this.enabledSubject.next(false);
    this.unreadSubject.next(0);
  }

  public refresh(): void {
    if (this.enabledSubject.value) {
      this.api.notifications.getUnreadCount().subscribe({
        error: (): void => undefined,
        next: (n: number): void => this.unreadSubject.next(n)
      });
      return;
    }
    // The feature state is unresolved (never attempted, or the last catalog call errored with
    // something other than 404) — retry rather than stay dead for the rest of the session on one
    // transient failure. A settled 404 leaves catalogKnown true and enabledSubject false, and must
    // not retry here. start() already guards against overlapping an in-flight catalog call.
    if (!this.catalogKnown) {
      this.start();
    }
  }

  /**
   * The product mark for a delivery row, resolved through the catalog (deliveries carry no product
   * column). Deliberately the same shape as `productOf` on the /notifications page so the bell and
   * the page render one source line from one source of truth. Returns undefined before the catalog
   * has arrived, which the template treats as "no mark" rather than rendering a placeholder.
   */
  public productOf(eventType: string): NotificationProduct | undefined {
    const entry: NotificationCatalogEntry | undefined = this.entryFor(eventType);
    return entry
      ? (this.catalog?.products ?? []).find((product: NotificationProduct): boolean => product.key === entry.product)
      : undefined;
  }

  /** The event's catalog label, falling back to the raw event type when the catalog is absent. */
  public labelFor(eventType: string): string {
    return this.entryFor(eventType)?.label ?? eventType;
  }

  private entryFor(eventType: string): NotificationCatalogEntry | undefined {
    return (this.catalog?.events ?? []).find(
      (event: NotificationCatalogEntry): boolean => event.eventType === eventType,
    );
  }
}
